import { EventCollectorProps } from 'hooks/eventCollector/types';
import { IconElement } from 'types/IconElement';
import { InteractiveElementProps } from 'types/InteractiveElementProps';
import { memo, ReactNode, useCallback, useMemo } from 'react';
import classes from 'classnames';
import LinkEvent from 'hooks/eventCollector/events/LinkEvent';
import styles from './Link.module.scss';
import useEventCollector from 'hooks/eventCollector/useEventCollector';

export type LinkProps = {
  href?: string;
  children: ReactNode;
  disabled?: boolean;
  className?: string;
  iconClassName?: string;
  target?: '_blank' | '_self' | '_parent' | '_top';
  dataTest?: string;
  leftIcon?: IconElement;
  rightIcon?: IconElement;
  textClassName?: string;
} & Omit<
  PartialBy<InteractiveElementProps<HTMLAnchorElement>, 'onClick'>,
  'loading'
>;

export const Link = memo(function Link({
  href,
  children,
  disabled = false,
  className,
  target = '_self',
  dataTest = 'link',
  leftIcon,
  rightIcon,
  onClick,
  textClassName,
  iconClassName,
  eventCollector,
}: LinkProps & EventCollectorProps) {
  const { track } = useEventCollector();

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      onClick?.(event);

      if (eventCollector?.dontTrack) {
        return;
      }

      const descInfo = href ? `link to ${href}` : 'link';

      track(
        new LinkEvent({
          content: children?.toString() ?? href ?? '',
          description: descInfo,
        }),
      );
    },
    [eventCollector?.dontTrack, children, onClick, href, track],
  );

  const renderIcons = useMemo(() => {
    return (
      <>
        {leftIcon && (
          <span className={classes(styles.icon, iconClassName)}>
            {leftIcon}
          </span>
        )}
        <span className={textClassName} data-test={`${dataTest}-content`}>
          {children}
        </span>
        {rightIcon && (
          <span className={classes(styles.icon, iconClassName)}>
            {rightIcon}
          </span>
        )}
      </>
    );
  }, [children, iconClassName, leftIcon, rightIcon]);

  if (disabled || !href)
    return (
      <span
        data-test={dataTest}
        data-testid={dataTest}
        className={classes(styles.link, disabled && styles.disabled, className)}
        onClick={handleClick}
      >
        {renderIcons}
      </span>
    );

  return (
    <a
      onClick={handleClick}
      data-test={dataTest}
      data-testid={dataTest}
      className={classes(styles.link, className)}
      href={href}
      target={target}
      rel="noopener"
    >
      {renderIcons}
    </a>
  );
});

export default Link;
